import React, { useMemo } from 'react'

import {
  AccountAvatar,
  Button,
  Drawer,
  Flex,
  Header,
  IconButton,
  InfoBadge,
  Link,
  Separator,
  Text,
  UserAvatar,
} from '@driftt/dds'
import { faArrowLeft } from '@driftt/dds.icons/solid/faArrowLeft'
import { faArrowRight } from '@driftt/dds.icons/solid/faArrowRight'
import { IconButton as TideIconButton } from '@driftt/tide-core'

import config from 'config'
import {
  DeprecatedClosedOpportunityTimelineCard,
  DeprecatedCreateOpportunityTimelineCard,
  Timeline,
} from 'layouts/RevenueReport/Opportunities/OpportunityTimeline/Timeline'
import { hasVisualOppotunityTimeline } from 'utils/access'
import { isNilOrEmpty } from 'utils/ramda'

import { getOpportunityStageVariant } from '../Opportunities.utils'

import { SalesforceIcon } from './OpportunityTimeline.assets'
import { getEventType } from './OpportunityTimeline.helpers'
import { useOpportunityTimeline } from './OpportunityTimeline.hooks'
import {
  StyledChatBubble,
  StyledDrawerContent,
  StyledStatusBadge,
} from './OpportunityTimeline.styled'
import {
  OpportunityTimelineProps,
  TimelineEventItem,
  TimelineEventOptionsEnum,
} from './OpportunityTimeline.types'
import { TimelineEvent } from './TimelineEvent'
import { TimelineSummaryCard } from './TimelineSummaryCard'

export const OpportunityTimeline = ({
  opportunity,
  onClose,
  isLoadingTableData,
  onPrevItem,
  onNextItem,
}: OpportunityTimelineProps) => {
  const hasRoiV3Access = hasVisualOppotunityTimeline()

  const { data: opportunityActivities, isLoading } = useOpportunityTimeline({
    opportunityId: opportunity?.opportunityId || '',
  })

  const stageVariant = getOpportunityStageVariant(
    `${opportunity?.opportunityStage}`
  )

  const opportunityEvents = useMemo(() => {
    const closeOpportunity: TimelineEventItem = {
      date: opportunity?.opportunityClosedAtWithZone || null, // NOTE: using closedAt to make an accurate sorting along with the dimension used to get all data sorted from cube
      component: hasRoiV3Access ? (
        <TimelineEvent.Root
          eventType="lifecycle"
          isLoading={isLoading}
          statusBadge={
            <StyledStatusBadge variant={stageVariant} size="small">
              {opportunity?.opportunityStage}
            </StyledStatusBadge>
          }
          date={`${opportunity?.opportunityClosedAtWithZone}`}
        >
          <TimelineEvent.Card>
            <TimelineEvent.Left>
              <SalesforceIcon />
            </TimelineEvent.Left>
            <Flex direction="column" gap={0}>
              <Link href="#" target="_blank" type="inline" size="small">
                <b>Salesforce</b>
              </Link>
              <Text variant="metadata">Opportunity closed</Text>
            </Flex>
          </TimelineEvent.Card>
        </TimelineEvent.Root>
      ) : (
        <DeprecatedClosedOpportunityTimelineCard
          stageVariant={stageVariant}
          opportunityStage={opportunity?.opportunityStage}
          opportunityClosedAtWithZone={opportunity?.opportunityClosedAtWithZone}
        />
      ),
    }

    const createOpportunity: TimelineEventItem = {
      date: opportunity?.opportunityCreatedAtWithZone || null, // NOTE: using closedAt to make an accurate sorting along with the dimension used to get all data sorted from cube
      component: hasRoiV3Access ? (
        <TimelineEvent.Root
          eventType="lifecycle"
          isLoading={isLoading}
          date={`${opportunity?.opportunityCreatedAtWithZone}`}
        >
          <TimelineEvent.Card>
            <TimelineEvent.Left>
              <SalesforceIcon />
            </TimelineEvent.Left>
            <Flex direction="column" gap={0}>
              <Link href="#" target="_blank" type="inline" size="small">
                <b>Salesforce</b>
              </Link>
              <Text variant="metadata">Opportunity created</Text>
            </Flex>
          </TimelineEvent.Card>
        </TimelineEvent.Root>
      ) : (
        <DeprecatedCreateOpportunityTimelineCard
          opportunityCreatedAtWithZone={
            opportunity?.opportunityCreatedAtWithZone
          }
        />
      ),
    }
    // Create your initial list of objects
    return [createOpportunity, closeOpportunity]
  }, [
    hasRoiV3Access,
    isLoading,
    opportunity?.opportunityClosedAtWithZone,
    opportunity?.opportunityCreatedAtWithZone,
    opportunity?.opportunityStage,
    stageVariant,
  ])

  const eventsList: TimelineEventItem[] = useMemo(() => {
    const events = opportunityActivities.map((activity, index) => {
      const endUserName =
        activity?.endUserContactName ||
        activity?.endUserDisplayName ||
        undefined

      const agentName = activity?.agentDisplayName

      //#region EVENT CARDS COMPONENTS

      const MeetingBookedEventCard = () => {
        const meetingTitle = 'WhiteWhale Demo' // TODO: replace with real one
        const meetingDate = 'October 17, 2023' // TODO: replace with real one
        const meetingTime = '11:00AM EST' // TODO: replace with real one

        return (
          <Flex direction="column" fullWidth gap={2}>
            <Flex direction="column" fullWidth gap={0}>
              <Header variant="h4">{meetingTitle}</Header>
              <Text variant="metadata">{meetingDate}</Text>
              <Text variant="metadata">{meetingTime}</Text>
            </Flex>
            <Separator />
            <Flex direction="column" fullWidth gap="half">
              <Flex fullWidth alignItems="center">
                <UserAvatar size={'x-small'} name={agentName} imageUrl={''} />
                <Text variant="bodyBold" truncate>
                  {agentName}
                </Text>
              </Flex>
              <Flex fullWidth alignItems="center">
                <UserAvatar size={'x-small'} name={endUserName} imageUrl={''} />
                <Text variant="bodyBold" truncate>
                  {endUserName}
                </Text>
              </Flex>
            </Flex>
          </Flex>
        )
      }

      const ConversationEventCard = () => {
        const message = activity?.messageBody

        return (
          <>
            <Flex direction="column" fullWidth gap={2}>
              <Flex direction="row">
                <UserAvatar name={endUserName} size="small" />
                <Flex direction="column" gap={0}>
                  <Link variant="inline" target="__blank" href={`#`}>
                    {endUserName}
                  </Link>
                  <Text variant="metadata">{endUserName}</Text>
                </Flex>
              </Flex>
              <Flex direction="column" fullWidth>
                {endUserName && <Text variant="metadata">{endUserName}</Text>}
                {message ? (
                  <StyledChatBubble>
                    <Text variant="metadata" color="$gray12">
                      {message}
                    </Text>
                  </StyledChatBubble>
                ) : undefined}
              </Flex>
            </Flex>
            {activity?.playbookId && (
              <Flex alignItems="center" justifyContent="flex-start">
                <TideIconButton icon="bot-2" />
                <Link
                  variant="subtle"
                  underline
                  target="__blank"
                  href={`${config.APP_URL}/reports/playbooks/chat/${activity?.playbookId}`}
                >
                  {activity?.playbookName}
                </Link>
              </Flex>
            )}
            {activity?.conversationId && (
              <Link
                href={`${config.APP_URL}/conversations/${activity?.conversationId}`}
                target="__blank"
                variant="inline"
                external={true}
              >
                View conversation
              </Link>
            )}
          </>
        )
      }

      const EmailCapturedEventCard = () => {
        return (
          <>
            {`Email captured ${
              activity?.agentDisplayName
                ? `by ${activity?.agentDisplayName}`
                : ''
            }`}
            <Text>
              {'Email: '}
              {activity?.endUserEmail}
            </Text>
          </>
        )
      }

      const eventType = getEventType(
        TimelineEventOptionsEnum[activity?.eventType]
      )

      const componentByType = {
        emailCaptured: <EmailCapturedEventCard />,
        meeting: <MeetingBookedEventCard />,
        conversation: <ConversationEventCard />,
      }

      //#endregion

      return {
        date: activity?.eventDateAt, // NOTE: same as conversation created at, shouldn't use with zone as it could lead to sorting issues
        component: (
          <TimelineEvent.Root
            eventType={eventType || ''}
            key={`timeline-event-card-${activity?.conversationId}-${index}`}
            isLoading={isLoading}
            statusBadge={
              <InfoBadge
                variant={activity?.leadCaptureSource ? 'secondary' : 'primary'}
              >
                {activity?.leadCaptureSource ? 'Sourced' : 'Influenced'}
              </InfoBadge>
            }
            date={activity?.eventDateAtWithZone}
          >
            <TimelineEvent.Card>
              <TimelineEvent.Content>
                {componentByType[eventType]}
              </TimelineEvent.Content>
            </TimelineEvent.Card>
          </TimelineEvent.Root>
        ),
      }
    })

    return [...opportunityEvents, ...events]
  }, [opportunityActivities, opportunityEvents, isLoading])

  // Sort the updated list by date
  const eventsSortedByDate = useMemo(() => {
    return eventsList.slice().sort((eventA, eventB) => {
      return (
        Number(new Date(eventB.date || '')) -
        Number(new Date(eventA.date || ''))
      )
    })
  }, [eventsList])

  return (
    <Drawer.Root
      open={!isNilOrEmpty(opportunity?.opportunityId)}
      onClose={onClose}
    >
      <Drawer.Dialog size="large" preventContentAutoFocusOnOpen={true}>
        <Drawer.Title
          prefix={
            <Flex direction="column" fullWidth alignItems="flex-start">
              <Flex
                direction="row"
                gap={2}
                fullWidth
                alignItems="center"
                justifyContent="flex-start"
              >
                <IconButton
                  title=""
                  icon={faArrowLeft}
                  onClick={onPrevItem}
                  disabled={isLoadingTableData}
                />
                <IconButton
                  title=""
                  icon={faArrowRight}
                  onClick={onNextItem}
                  disabled={isLoadingTableData}
                />
              </Flex>
              <Flex
                direction="row"
                gap={1}
                fullWidth
                alignItems="center"
                justifyContent="flex-start"
              >
                <AccountAvatar name={opportunity?.companyName} size="medium" />
                <Flex
                  direction="column"
                  gap={0}
                  fullWidth
                  alignItems="flex-start"
                  justifyContent="center"
                >
                  <Header variant="h3">{opportunity?.companyName}</Header>
                  <Link
                    href="https://www.drift.com"
                    target="_blank"
                    type="inline"
                    size="small"
                  >
                    {opportunity?.opportunityDomain}
                  </Link>
                </Flex>
              </Flex>
            </Flex>
          }
          title=""
        />
        <StyledDrawerContent>
          <Flex gap={2} direction="column">
            <TimelineSummaryCard
              title={opportunity?.opportunityName || '--'}
              isInfluenced={!!opportunity?.isInfluenced}
              isSourced={!!opportunity?.isSourced}
              type={`${opportunity?.opportunityType}`}
              totalArr={`${opportunity?.opportunityAmount}`}
              opportunityStage={`${opportunity?.opportunityStage}`}
              createdDate={`${opportunity?.opportunityCreatedAtWithZone}`}
              closedDate={`${opportunity?.opportunityClosedAtWithZone}`}
              // TODO: ciip1996 pass the correct lastUpdateDate value from BE
              lastUpdateDate={`${opportunity?.opportunityClosedAtWithZone}`}
            />
            {/* RENDER SKELETONS WHEN LOADING */}
            {isLoading &&
              Array(6)
                .fill(1)
                .map((_, index) => (
                  <TimelineEvent.Root
                    eventType="meeting"
                    key={`timeline-event-template-skeleton-${index}`}
                    date=""
                    statusBadge={null}
                    isLoading={true}
                  />
                ))}
            {/* RENDER ALL EVENTS */}
            {hasRoiV3Access ? (
              // Gated access to the new timeline
              <Timeline events={eventsSortedByDate} />
            ) : (
              eventsList.map((event) => {
                return event.component
              })
            )}
          </Flex>
        </StyledDrawerContent>
        <Drawer.Actions>
          <Flex fullWidth justifyContent="flex-end" alignItems="center">
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
          </Flex>
        </Drawer.Actions>
      </Drawer.Dialog>
    </Drawer.Root>
  )
}
